// What a belt part is doing on screen, apart from what the rules say it is.
//
// Pure, and no Three.js: everything here is a number per square eased toward
// another number, and keeping it out of `industryViews` is what makes it
// testable at all — that class needs GLTF models to construct, so anything
// living inside it is only ever verified by looking.
//
// Four things move. A part **grows** in when it is laid and **shrinks** out
// when it is lifted; it **turns** when it is re-pointed; and it **squashes**
// when a piece stands on it. The first three settle; the fourth bounces, which
// is the one thing here that is not an exponential ease.

/** Deck height a part is drawn at. `industryViews` places against it. */
export const BELT_GROW_SECONDS = 0.22;

/**
 * How flat a part goes with a piece standing on it.
 *
 * The settled value, which the spring below aims at rather than snaps to.
 */
export const SHARED_SQUASH = 0.34;

/**
 * The squash spring: stiffness, and how much of the bounce survives.
 *
 * **Underdamped on purpose**, which is the whole difference between this and
 * every other eased value here. A critically-damped approach is what you want
 * for a rotation or a heap drawing down — it arrives and stops, and any
 * overshoot would read as an error being corrected. A deck taking a piece's
 * weight is the opposite: the overshoot *is* the reading, because it is what
 * says the thing has mass and the belt has give.
 *
 * Tuned to about two visible bounces. `DAMPING` at `2*sqrt(STIFFNESS)` is
 * critical damping and kills it entirely; the ratio here is near 0.35.
 */
const STIFFNESS = 420;
const DAMPING = 14;

/**
 * How far a squashed deck spreads sideways, as a share of what it lost.
 *
 * Squash and stretch: a deck pressed to a third of its height that kept its
 * footprint reads as a texture change rather than as something being
 * compressed. Not volume-preserving (`1/sqrt(y)`, which at `SHARED_SQUASH`
 * would be a 71% wider part overhanging its neighbours) — a fraction of it, so
 * the bulge is legible and stays on its own square.
 */
const SPREAD = 0.22;

/** The most a deck may spread, however hard the spring overshoots. */
const SPREAD_CAP = 0.18;

export interface BeltAnim {
  /** Is there a part here at all, as far as the animation is concerned. */
  present: boolean;
  /** 0..1 while a new part grows in, and back down while a lifted one goes. */
  grow: number;
  /**
   * Drawn after the part is gone from the rules, while `grow` runs out.
   *
   * The draw pass sweeps the board and asks the *rules* whether a square has a
   * part, so a lifted one is simply not drawn — which is why the shrink that
   * was already written here had never once played. This is the flag that
   * keeps a square in the pass for the fifth of a second it takes to go.
   */
  leaving: boolean;
  /** The owner the leaving part had, since the rules no longer know. */
  owner: number;
  /** Displayed yaw, easing toward the real one so a rotate turns. */
  yaw: number;
  targetYaw: number;
  /** Displayed heap size, easing so mining draws the pile down. */
  heap: number;
  targetHeap: number;
  /** Displayed height factor under a piece: 1 bare, `SHARED_SQUASH` loaded. */
  squash: number;
  /** The spring's velocity. Zero at rest; this is what makes it bounce. */
  squashVel: number;
  targetSquash: number;
}

export const newBeltAnim = (): BeltAnim => ({
  present: false,
  grow: 1,
  leaving: false,
  owner: 0,
  yaw: 0,
  targetYaw: 0,
  heap: 0,
  targetHeap: 0,
  squash: 1,
  squashVel: 0,
  targetSquash: 1
});

/**
 * The equivalent of `to` nearest `from`, so easing a rotation takes the short
 * way round instead of unwinding most of a circle when the angle wraps.
 */
export function nearestAngle(from: number, to: number): number {
  const turns = Math.round((from - to) / (2 * Math.PI));
  return to + turns * 2 * Math.PI;
}

/** A piece has arrived on this part, or left it. Sets the spring's target. */
export function setLoaded(state: BeltAnim, loaded: boolean): void {
  state.targetSquash = loaded ? SHARED_SQUASH : 1;
}

/**
 * Advance one square's animation. True while anything is still moving.
 *
 * Fixed sub-steps for the spring, because it is the one thing here that is not
 * unconditionally stable: `STIFFNESS` at 420 diverges above roughly a 60ms
 * frame, and a dropped frame is exactly when that happens. The eased values do
 * not care — an exponential approach cannot overshoot — so only the spring
 * pays for it.
 */
export function stepBeltAnim(state: BeltAnim, dt: number): boolean {
  let busy = false;
  const step = Math.min(1, dt * 5);

  // **`present`, not merely "not leaving".** A part that has finished
  // shrinking out is neither, and it sits at `grow` 0 for good — without this
  // the frame after it vanished it started growing back, because "grow is
  // under one" is true of something that is gone as well as of something that
  // is arriving.
  if (state.present && state.grow < 1) {
    state.grow = Math.min(1, state.grow + dt / BELT_GROW_SECONDS);
    busy = true;
  }
  if (state.leaving) {
    state.grow = Math.max(0, state.grow - dt / BELT_GROW_SECONDS);
    // Gone. The entry stays — the map is keyed by square and a square may take
    // another part later — but it stops being drawn.
    if (state.grow <= 0) state.leaving = false;
    busy = true;
  }

  if (Math.abs(state.yaw - state.targetYaw) > 1e-3) {
    state.yaw += (state.targetYaw - state.yaw) * step;
    busy = true;
  } else {
    state.yaw = state.targetYaw;
  }

  if (Math.abs(state.heap - state.targetHeap) > 1e-3) {
    state.heap += (state.targetHeap - state.heap) * step;
    busy = true;
  } else {
    state.heap = state.targetHeap;
  }

  if (stepSquash(state, dt)) busy = true;
  return busy;
}

const MAX_SPRING_STEP = 1 / 120;

function stepSquash(state: BeltAnim, dt: number): boolean {
  const settled =
    Math.abs(state.squash - state.targetSquash) < 1e-3 && Math.abs(state.squashVel) < 1e-3;
  if (settled) {
    state.squash = state.targetSquash;
    state.squashVel = 0;
    return false;
  }

  let left = Math.min(dt, 0.25);
  while (left > 0) {
    const h = Math.min(MAX_SPRING_STEP, left);
    const accel = STIFFNESS * (state.targetSquash - state.squash) - DAMPING * state.squashVel;
    state.squashVel += accel * h;
    state.squash += state.squashVel * h;
    left -= h;
  }
  return true;
}

/**
 * How the deck is scaled right now: flat under a piece, wider for it.
 *
 * `grow` multiplies all three, so a part being laid or lifted keeps its shape
 * while it arrives. The sideways spread comes off the squash rather than being
 * animated on its own, so the two cannot drift apart — and it is clamped,
 * because the spring overshoots below `SHARED_SQUASH` on the way in and an
 * uncapped bulge would reach onto the neighbouring square.
 */
export function deckScale(
  state: BeltAnim,
  /** A part that is switched off sinks, which is a separate reading. */
  running: boolean
): { x: number; y: number; z: number } {
  const spread = Math.min(SPREAD_CAP, Math.max(0, (1 - state.squash) * SPREAD));
  const wide = (1 + spread) * state.grow;
  return {
    x: wide,
    y: (running ? 1 : 0.55) * state.squash * state.grow,
    z: wide
  };
}
